import store from '../store.js'

class AvatarManager {
    constructor() {
        this.cache = new Map() // userId -> { url: string, data: blob, timestamp: number }
        this.pendingDownloads = new Map() // userId -> Promise
        this.subscribers = new Map() // userId -> Set of callback functions
        this.cacheExpiry = 5 * 60 * 1000 // 5 minutes in milliseconds
    }

    /**
     * Get avatar for a user - returns initials immediately, updates with image when available
     * @param {Object} user - User object with id and name properties
     * @param {Function} callback - Callback function to be called when avatar is ready
     * @returns {string} User initials as fallback
     */
    getAvatar(user, callback = null) {
        if (!user || !user.id || !user.name) {
            return '?'
        }

        const userId = user.id
        const initials = this.getInitials(user.name)

        // If we have a cached image, return it immediately
        if (this.cache.has(userId)) {
            const cached = this.cache.get(userId)
            if (Date.now() - cached.timestamp < this.cacheExpiry) {
                if (callback) {
                    callback(cached.url)
                }
                return initials
            } else {
                // Cache expired, remove it
                this.cache.delete(userId)
            }
        }

        // If there's already a pending download, just subscribe to it
        if (this.pendingDownloads.has(userId)) {
            if (callback) {
                this.subscribeToAvatar(userId, callback)
            }
            return initials
        }

        // Start a new download
        this.downloadAvatar(userId, callback)
        return initials
    }

    /**
     * Download avatar for a user
     * @param {string|number} userId - User ID
     * @param {Function} callback - Callback function
     */
    async downloadAvatar(userId, callback = null) {
        // Create a promise for this download
        const downloadPromise = this.performDownload(userId)
        this.pendingDownloads.set(userId, downloadPromise)

        try {
            const result = await downloadPromise
            
            if (result.success) {
                // Cache the successful result
                this.cache.set(userId, {
                    url: result.url,
                    data: result.data,
                    timestamp: Date.now()
                })
                
                // Notify all subscribers
                this.notifySubscribers(userId, result.url)
            }
        } catch (error) {
            console.warn(`Failed to download avatar for user ${userId}:`, error)
        } finally {
            // Clean up pending download
            this.pendingDownloads.delete(userId)
        }
    }

    /**
     * Perform the actual download
     * @param {string|number} userId - User ID
     * @returns {Promise} Download result
     */
    async performDownload(userId) {
        try {
            const response = await store.dispatch('users/getUserAvatar', userId)
            
            if (response && response.data) {
                const url = URL.createObjectURL(response.data)
                return {
                    success: true,
                    url: url,
                    data: response.data
                }
            } else {
                throw new Error('No data received')
            }
        } catch (error) {
            return {
                success: false,
                error: error
            }
        }
    }

    /**
     * Subscribe to avatar updates for a user
     * @param {string|number} userId - User ID
     * @param {Function} callback - Callback function
     */
    subscribeToAvatar(userId, callback) {
        if (!this.subscribers.has(userId)) {
            this.subscribers.set(userId, new Set())
        }
        this.subscribers.get(userId).add(callback)
    }

    /**
     * Notify all subscribers for a user
     * @param {string|number} userId - User ID
     * @param {string} avatarUrl - Avatar URL
     */
    notifySubscribers(userId, avatarUrl) {
        if (this.subscribers.has(userId)) {
            this.subscribers.get(userId).forEach(callback => {
                try {
                    callback(avatarUrl)
                } catch (error) {
                    console.warn('Error in avatar callback:', error)
                }
            })
            // Clear subscribers after notification
            this.subscribers.delete(userId)
        }
    }

    /**
     * Get user initials from name
     * @param {string} name - User's full name
     * @returns {string} User initials
     */
    getInitials(name) {
        if (!name) return '?'
        
        const names = name.trim().split(" ")
        return names.length > 1 
            ? names[0].charAt(0).toUpperCase() + names[1].charAt(0).toUpperCase()
            : names[0].charAt(0).toUpperCase()
    }

    /**
     * Clear cache for a specific user or all users
     * @param {string|number} userId - Optional user ID to clear specific cache
     */
    clearCache(userId = null) {
        if (userId) {
            const cached = this.cache.get(userId)
            if (cached && cached.url) {
                URL.revokeObjectURL(cached.url)
            }
            this.cache.delete(userId)
        } else {
            // Clear all cached URLs
            this.cache.forEach(cached => {
                if (cached.url) {
                    URL.revokeObjectURL(cached.url)
                }
            })
            this.cache.clear()
        }
    }

    /**
     * Get cached avatar URL if available
     * @param {string|number} userId - User ID
     * @returns {string|null} Cached avatar URL or null
     */
    getCachedAvatar(userId) {
        if (this.cache.has(userId)) {
            const cached = this.cache.get(userId)
            if (Date.now() - cached.timestamp < this.cacheExpiry) {
                return cached.url
            } else {
                this.cache.delete(userId)
            }
        }
        return null
    }
}

// Create singleton instance
const avatarManager = new AvatarManager()

export default avatarManager
